import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, merge, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {
  AsBuiltStatus,
  DrawingAsBuiltListParams,
  DrawingAsBuiltOffender,
  DrawingAsBuiltPage,
  DrawingAsBuiltRow,
} from '../../../../../../../../shared/models/drawing-sheet';
import { MarkupService } from '../../../../../../../../shared/services/markup.service';
import { AuthService } from '../../../../../../../../shared/services/auth.service';
import { ProjectDrawingsService } from '../../../../../../../../shared/services/project-drawings.service';
import { AS_BUILT_ACTION_COPY, AS_BUILT_STATUS_COPY, deleteMarkMessage, MarkCrop, markCrop, reviewActions } from '../drawing-as-built';
import { markRecordRoute, markRecordText } from '../drawing-markers';
import { Router } from '@angular/router';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { DrawingsAnalytics } from '../drawings-analytics';

/** The tab's filters, kept in the URL so Back from a sheet returns to them */
export interface AsBuiltFilters {
  status: AsBuiltStatus | '';
  sheet: string;
  /** A revision of that sheet ('' for all) */
  revision: string;
  q: string;
}

/** What a bulk review may set (Mark incorporated needs a revision per sheet: one mark at a time, on the sheet) */
export type BulkAction = 'reviewed' | 'rejected' | 'open';

const BULK_ACTIONS: BulkAction[] = ['reviewed', 'rejected', 'open'];
const STATUSES: AsBuiltStatus[] = ['open', 'reviewed', 'rejected', 'incorporated'];
const BULK_DONE: Record<BulkAction, string> = { reviewed: 'marked reviewed', rejected: 'rejected', open: 'reopened' };
/** Thumbnail box, width / height (64 × 48) */
const THUMB_ASPECT = 4 / 3;

/**
 * The as-built record set (spec §15 rule 7): every as-built mark on any revision of the project's sheets, filtered by
 * status, sheet and search, 20 a page. Publishers select marks and review them together; the API applies a bulk
 * review all or nothing and names every mark that could not change.
 */
@Component({
  selector: 'app-drawings-as-built',
  templateUrl: './drawings-as-built.component.html',
  styleUrls: ['./drawings-as-built.component.scss'],
  standalone: false,
})
export class DrawingsAsBuiltComponent implements OnInit, OnChanges, OnDestroy {
  /** The app's toast (Figma 11893:79195): CommonService.notification, no action button */
  private readonly notify = inject(CommonService);
  /** W48: PostHog events */
  private readonly track = inject(DrawingsAnalytics);
  private readonly router = inject(Router);
  @Input() projectId: string;
  @Input() canPublish = false;
  /** Configure: Delete for marks that are no longer open (spec §15 rule 5, W30) */
  @Input() canConfigure = false;
  /** The status filter to show (the register's "waiting for review" link sets open) */
  @Input() status: AsBuiltStatus | '' = '';
  /** Sheet and search to start with (from the URL, after Back from a sheet) */
  @Input() initialSheet = '';
  @Input() initialQuery = '';
  @Input() initialRevision = '';
  @Output() openMark = new EventEmitter<{ revision: string; mark: string }>();
  /** Project-wide counts per status (only from loads with no sheet or search filter), for the register's signal */
  @Output() countsChange = new EventEmitter<Record<AsBuiltStatus, number>>();
  /** Every filter change, for the register's URL */
  @Output() filtersChange = new EventEmitter<AsBuiltFilters>();

  readonly pageSize = 20;
  readonly statuses = STATUSES;
  readonly bulkActions = BULK_ACTIONS;
  readonly statusCopy = AS_BUILT_STATUS_COPY;
  readonly actionCopy = AS_BUILT_ACTION_COPY;

  rows: DrawingAsBuiltRow[] = [];
  totalRecords = 0;
  counts: Record<AsBuiltStatus, number> = { open: 0, reviewed: 0, rejected: 0, incorporated: 0 };
  pageIndex = 0;
  loading = false;
  statusFilter: AsBuiltStatus | '' = '';
  sheet = new FormControl('');
  /** One revision of the chosen sheet (the API matches "Rev C" only by label, so search cannot single it out) */
  revision = new FormControl('');
  /** The chosen sheet's revisions, for the Revision filter */
  revisions: { uuid: string; label: string | null }[] = [];
  search = new FormControl('');
  /** Sheet filter options (the register's first 200 sheets) */
  sheets: { uuid: string; number: string }[] = [];
  /** Selected marks on this page (publishers) */
  selected = new Set<string>();
  /** The bulk review being confirmed */
  bulkAction: BulkAction | null = null;
  /** The bulk review comment, in the app's text area (app-text-area); typing clears the error */
  readonly bulkForm = new FormGroup({ comment: new FormControl('') });
  private readonly bulkCommentTyped = this.bulkForm.controls.comment.valueChanges.subscribe(() => (this.bulkError = ''));
  bulkError = '';
  private bulkSaving = false;
  /** The marks a refused bulk review named; the rest can be sent again without them */
  offenders: { uuid: string; label: string; reason: string }[] = [];
  /** Bumped by every counts emission or refresh, so a slow refresh never overwrites newer counts */
  private countsSeq = 0;
  /** The marks the last bulk request named */
  private sentItems: string[] = [];

  /** The Sheet pill's value: the chosen sheet's number, or All */
  get sheetLabel(): string {
    return (this.sheet.value && this.sheets.find(s => s.uuid === this.sheet.value)?.number) || 'All';
  }

  /** The Revision pill's value */
  get revisionLabel(): string {
    const r = this.revision.value ? this.revisions.find(x => x.uuid === this.revision.value) : null;
    return !r ? 'All' : r.label ? `Rev ${r.label}` : 'Unlabelled';
  }
  /** Page aspect per mark (its page image or strokes PNG, both page-sized), once a thumbnail image has loaded */
  private readonly aspects = new Map<string, number>();
  /** Thumbnail images that failed to load (an expired page image): dropped, the strokes stay */
  private readonly failedImages = new Set<string>();
  private readonly loads = new Subject<DrawingAsBuiltListParams>();
  private readonly onDestroy = new Subject<void>();
  private readonly accountUuid: string | null;

  constructor(
    private api: ProjectDrawingsService,
    private markup: MarkupService,
    private dialog: MatDialog,
    auth: AuthService,
  ) {
    this.accountUuid = auth.getCurrentUser()?.id ?? null;
  }

  get selectedRows(): DrawingAsBuiltRow[] {
    return this.rows.filter(r => this.selected.has(r.uuid));
  }

  get allSelected(): boolean {
    return !!this.rows.length && this.rows.every(r => this.selected.has(r.uuid));
  }

  get countAll(): number {
    return STATUSES.reduce((n, s) => n + this.counts[s], 0);
  }

  /** Selected marks this action can change; Apply sends only these */
  eligible(action: BulkAction | null): DrawingAsBuiltRow[] {
    return action ? this.selectedRows.filter(r => reviewActions(r.review_status).includes(action)) : [];
  }

  /** The marks a refused review left out, which can be sent again */
  get retryable(): string[] {
    const out = new Set(this.offenders.map(o => o.uuid));
    return this.sentItems.filter(uuid => !out.has(uuid));
  }

  get bulkComment(): string {
    return this.bulkForm.controls.comment.value ?? '';
  }

  set bulkComment(value: string) {
    this.bulkForm.controls.comment.setValue(value, { emitEvent: false });
  }

  /** A bulk review on its way: the comment cannot change under it */
  get bulkBusy(): boolean {
    return this.bulkSaving;
  }

  set bulkBusy(busy: boolean) {
    this.bulkSaving = busy;
    const comment = this.bulkForm.controls.comment;
    if (busy) {
      comment.disable({ emitEvent: false });
    } else {
      comment.enable({ emitEvent: false });
    }
  }

  ngOnInit(): void {
    this.statusFilter = this.status || '';
    this.sheet.setValue(this.initialSheet || '', { emitEvent: false });
    this.search.setValue(this.initialQuery || '', { emitEvent: false });
    // Its option appears once the sheet's revisions load; the select picks it up then
    this.revision.setValue(this.initialSheet ? this.initialRevision || '' : '', { emitEvent: false });
    this.loads
      .pipe(
        switchMap(qp =>
          this.api.listAsBuilt(this.projectId, qp).pipe(
            map(page => ({ page, projectWide: !qp.sheet && !qp.q && !qp.revision_uuid })),
            catchError(e => {
              this.loading = false;
              this.fail(e);
              return EMPTY;
            }),
          ),
        ),
        takeUntil(this.onDestroy),
      )
      .subscribe(({ page, projectWide }: { page: DrawingAsBuiltPage; projectWide: boolean }) => {
        this.rows = page.data;
        this.totalRecords = page.totalRecords;
        this.counts = page.counts;
        this.loading = false;
        const shown = new Set(page.data.map(r => r.uuid));
        [...this.aspects.keys()].filter(uuid => !shown.has(uuid)).forEach(uuid => this.aspects.delete(uuid));
        // New rows get another try at images that failed before (a transient error, or a fresh signed URL)
        this.failedImages.clear();
        // Counts follow the sheet and search filters: only an unfiltered load speaks for the whole project.
        if (projectWide) {
          this.countsSeq++;
          this.countsChange.emit(page.counts);
        }
      });
    this.api.listSheets(this.projectId, { start: 0, total: 200 }).pipe(takeUntil(this.onDestroy)).subscribe({
      next: page => (this.sheets = page.data.map(s => ({ uuid: s.uuid, number: s.number }))),
      // Only the filter's options: the list works without them.
      error: () => (this.sheets = []),
    });
    this.sheet.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(sheet => {
      // A revision belongs to one sheet: a new sheet starts from all its revisions
      this.revision.setValue('', { emitEvent: false });
      this.loadRevisions(sheet || '');
    });
    if (this.sheet.value) {
      this.loadRevisions(this.sheet.value);
    }
    merge(this.search.valueChanges.pipe(debounceTime(300), distinctUntilChanged()), this.sheet.valueChanges, this.revision.valueChanges)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.reload());
    this.load();
  }

  /** The register sets filters (its waiting-for-review link): apply them once, when they differ from what is shown. */
  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some(c => c.firstChange)) {
      return;
    }
    let changed = false;
    if (changes.status && (this.status || '') !== this.statusFilter) {
      this.statusFilter = this.status || '';
      changed = true;
    }
    if (changes.initialSheet && (this.initialSheet || '') !== (this.sheet.value || '')) {
      this.sheet.setValue(this.initialSheet || '', { emitEvent: false });
      changed = true;
    }
    if (changes.initialQuery && (this.initialQuery || '') !== (this.search.value?.trim() || '')) {
      this.search.setValue(this.initialQuery || '', { emitEvent: false });
      changed = true;
    }
    if (changes.initialRevision && (this.initialRevision || '') !== (this.revision.value || '')) {
      this.revision.setValue(this.initialRevision || '', { emitEvent: false });
      changed = true;
    }
    if (changed) {
      this.reload();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  setStatus(status: AsBuiltStatus | ''): void {
    this.statusFilter = status;
    this.reload();
  }

  page(e: { pageIndex: number }): void {
    this.pageIndex = e.pageIndex;
    this.clearSelection();
    this.load();
  }

  load(): void {
    this.loading = true;
    const qp: DrawingAsBuiltListParams = { start: this.pageIndex * this.pageSize, total: this.pageSize };
    if (this.statusFilter) {
      qp.status = this.statusFilter;
    }
    // A revision only means something within its sheet
    if (this.sheet.value && this.revision.value) {
      qp.revision_uuid = this.revision.value;
    }
    if (this.sheet.value) {
      qp.sheet = this.sheet.value;
    }
    if (this.search.value?.trim()) {
      qp.q = this.search.value.trim();
    }
    this.loads.next(qp);
  }

  // --- Rows

  open(row: DrawingAsBuiltRow): void {
    this.openMark.emit({ revision: row.revision.uuid, mark: row.uuid });
  }

  /** 7.2: "From <record>" for a mark made from a record (or "From a deleted issue"); '' otherwise */
  recordText(row: DrawingAsBuiltRow): string {
    return markRecordText(row.linked_record);
  }

  /** 7.2: the record a mark was made from opens from its row, when the reader can open it */
  canOpenRecord(row: DrawingAsBuiltRow): boolean {
    return !!markRecordRoute(this.projectId, row.linked_record);
  }

  openRecord(row: DrawingAsBuiltRow, event?: Event): void {
    event?.stopPropagation();
    const route = markRecordRoute(this.projectId, row.linked_record);
    if (route) {
      void this.router.navigate(route.commands, { queryParams: route.queryParams });
    }
  }

  comment(row: DrawingAsBuiltRow): string {
    return row.last_review?.comment ?? '';
  }

  /** The thumbnail's image layers, page first: the strokes alone when the listing has no page image (W31) */
  thumbLayers(row: DrawingAsBuiltRow): string[] {
    return [row.page_preview_url, row.preview_url].filter((url): url is string => !!url && !this.failedImages.has(url));
  }

  onThumbError(url: string): void {
    this.failedImages.add(url);
  }

  onThumbLoad(row: DrawingAsBuiltRow, img: HTMLImageElement): void {
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      this.aspects.set(row.uuid, img.naturalWidth / img.naturalHeight);
    }
  }

  /** The thumbnail zoomed to the mark once its aspect is known; the whole strokes image until then */
  crop(row: DrawingAsBuiltRow): MarkCrop | null {
    return markCrop(row.bbox, this.aspects.get(row.uuid) ?? 0, THUMB_ASPECT);
  }

  /** As on the sheet card: the author's own open mark (not on a withdrawn revision), or configure once it is not open */
  canDelete(row: DrawingAsBuiltRow): boolean {
    if (row.review_status === 'open') {
      return !!this.accountUuid && row.created_by?.uuid === this.accountUuid && row.revision.status !== 'withdrawn';
    }
    return this.canConfigure;
  }

  remove(row: DrawingAsBuiltRow): void {
    if (!this.canDelete(row)) {
      return;
    }
    const model = new ConfirmDialogModel('Delete as-built mark', deleteMarkMessage(row.sheet.number, row.revision.label), 'Delete', 'Cancel', true);
    this.dialog.open(ConfirmDialogComponent, { data: model }).afterClosed().pipe(takeUntil(this.onDestroy)).subscribe(ok => {
      if (!ok) {
        return;
      }
      this.markup.delete(row.uuid).pipe(takeUntil(this.onDestroy)).subscribe({
        next: () => {
          this.notify.notification('As-built mark deleted', 'success');
          this.selected.delete(row.uuid);
          this.load();
          this.refreshProjectCounts();
        },
        error: e => this.fail(e),
      });
    });
  }

  // --- Selection and bulk review (publishers)

  toggle(row: DrawingAsBuiltRow, on: boolean): void {
    if (on) {
      this.selected.add(row.uuid);
    } else {
      this.selected.delete(row.uuid);
    }
    this.afterSelection();
  }

  toggleAll(on: boolean): void {
    this.selected = new Set(on ? this.rows.map(r => r.uuid) : []);
    this.afterSelection();
  }

  clearSelection(): void {
    this.selected = new Set();
    this.cancelBulk();
  }

  /** How many selected marks this action can change */
  canTake(action: BulkAction): number {
    return this.eligible(action).length;
  }

  startBulk(action: BulkAction): void {
    if (!this.canPublish || !this.selected.size || !this.canTake(action)) {
      return;
    }
    this.bulkAction = action;
    this.bulkComment = '';
    this.bulkError = '';
    this.offenders = [];
  }

  cancelBulk(): void {
    this.bulkAction = null;
    this.bulkComment = '';
    this.bulkError = '';
    this.offenders = [];
  }

  /**
   * Sends the review for the selected marks that can take it (or `items`, after a refusal: the ones that can change).
   * The API is all or nothing, so a mark whose status changed meanwhile still comes back as a 409 with offenders.
   */
  confirmBulk(items: string[] = this.eligible(this.bulkAction).map(r => r.uuid)): void {
    const action = this.bulkAction;
    const comment = this.bulkComment.trim();
    if (!action || !items.length || this.bulkBusy) {
      return;
    }
    if (action === 'rejected' && !comment) {
      this.bulkError = 'Say why the marks are rejected.';
      return;
    }
    this.bulkBusy = true;
    this.bulkError = '';
    this.sentItems = items;
    this.api.bulkReviewAsBuilt(this.projectId, { items, status: action, ...(comment ? { comment } : {}) }).pipe(takeUntil(this.onDestroy)).subscribe({
      next: changed => {
        this.bulkBusy = false;
        const n = changed.length || items.length;
        this.notify.notification(`${n} as-built ${n === 1 ? 'mark' : 'marks'} ${BULK_DONE[action]}`, 'success');
        this.track.track('as_built_reviewed', { status: action, count: n, source: 'list' });
        this.clearSelection();
        this.load();
        this.refreshProjectCounts();
      },
      error: e => {
        this.bulkBusy = false;
        const offenders: DrawingAsBuiltOffender[] = Array.isArray(e?.error?.offenders) ? e.error.offenders : [];
        if (e?.status === 409 && offenders.length) {
          // All or nothing: nothing changed. Name each mark; the rest can go again without them.
          this.offenders = offenders.map(o => ({ uuid: o.uuid, label: this.markLabel(o.uuid), reason: this.reasonText(o) }));
          this.bulkError = `${offenders.length === 1 ? '1 mark' : `${offenders.length} marks`} could not change, so nothing was changed.`;
          return;
        }
        this.bulkError = this.api.failureMessage(e?.error?.code, this.api.errorMessage(e));
      },
    });
  }

  retryWithoutOffenders(): void {
    const rest = this.retryable;
    if (rest.length) {
      this.confirmBulk(rest);
    }
  }

  /**
   * After a change made under a sheet or search filter, the reload's counts are filtered and not passed on: ask once for
   * the project-wide counts so the register's signal and tab count follow (an unfiltered reload passes them on itself).
   */
  private refreshProjectCounts(): void {
    if (!this.sheet.value && !this.search.value?.trim() && !this.revision.value) {
      return;
    }
    const seq = ++this.countsSeq;
    this.api.listAsBuilt(this.projectId, { start: 0, total: 1 }).pipe(takeUntil(this.onDestroy)).subscribe({
      next: page => {
        if (seq === this.countsSeq) {
          this.countsChange.emit(page.counts);
        }
      },
      // Only the register's hint: it catches up on the next unfiltered load.
      error: () => undefined,
    });
  }

  private loadRevisions(sheetUuid: string): void {
    this.revisions = [];
    if (!sheetUuid) {
      return;
    }
    this.api.getSheet(this.projectId, sheetUuid).pipe(takeUntil(this.onDestroy)).subscribe({
      next: s => {
        if (this.sheet.value === sheetUuid) {
          this.revisions = (s?.revisions ?? []).map(r => ({ uuid: r.uuid, label: r.label }));
        }
      },
      // Only the filter's options: without them the sheet filter still works
      error: () => (this.revisions = []),
    });
  }

  private reload(): void {
    this.pageIndex = 0;
    this.clearSelection();
    this.filtersChange.emit({ status: this.statusFilter, sheet: this.sheet.value || '', revision: this.revision.value || '', q: this.search.value?.trim() || '' });
    this.load();
  }

  /** A changed selection drops a refusal's list; an empty one also drops the action being confirmed */
  private afterSelection(): void {
    this.offenders = [];
    if (!this.selected.size) {
      this.cancelBulk();
    }
  }

  private markLabel(uuid: string): string {
    const row = this.rows.find(r => r.uuid === uuid);
    if (!row) {
      return 'A mark no longer listed';
    }
    return [`${row.sheet.number} Rev ${row.revision.label ?? ''}`.trim(), row.created_by?.name, this.statusCopy[row.review_status]].filter(Boolean).join(' · ');
  }

  /** The API's reason names the status ("An as-built mark that is incorporated cannot be set to reviewed.") */
  private reasonText(o: DrawingAsBuiltOffender): string {
    return o.reason || this.api.failureMessage(o.code);
  }

  private fail(e: { error?: { code?: string } } | null): void {
    this.track.error('as_built', e);
    this.notify.notification(this.api.failureMessage(e?.error?.code, this.api.errorMessage(e)), 'error');
  }
}
